import { Button, Flex } from '@aws-amplify/ui-react';
import { TbInfoCircle } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import Banner from '../../custom-components/Banner';

interface CreateExportBarProps {
  institutionsIncluded: number;
  exportLimit?: number;
  isCreating: boolean;
  onCreateExport: () => void;
}

/**
 * The hand-off to the export wizard, with the institution export limit surfaced here.
 *
 * The limit is otherwise enforced during the export's review step, which means a customer
 * completes the whole wizard and waits minutes before being told the export is too large.
 * Checking it against the selection they can still change turns a dead end into an
 * instruction: narrow the filter and export a slice.
 */
export function CreateExportBar({
  institutionsIncluded,
  exportLimit,
  isCreating,
  onCreateExport,
}: CreateExportBarProps) {
  const overLimit = exportLimit != null && institutionsIncluded > exportLimit;
  const approaching = !overLimit && exportLimit != null && institutionsIncluded >= exportLimit * 0.9;

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
      {/* One flex child, so the text and link flow inline rather than being laid out
          beside each other as rows. */}
      {overLimit && (
        <Banner color='yellow'>
          <span>
            You've selected {institutionsIncluded.toLocaleString()} institutions, but your plan
            allows {exportLimit!.toLocaleString()} per export.{' '}
            <Link to='/settings/service-limits'>View your service limits</Link>
          </span>
        </Banner>
      )}

      {approaching && (
        <Banner color='blue' size='small'>
          {institutionsIncluded.toLocaleString()} of your {exportLimit!.toLocaleString()}-institution
          export limit.
        </Banner>
      )}

      <Flex alignItems='center' justifyContent='flex-end' gap={8} style={{ marginTop: 12 }}>
        {/* Creating the export is the point of no return in appearance only - it opens the
            wizard rather than writing anything - and saying so is what makes the button
            safe to press. */}
        <TbInfoCircle
          size={16}
          style={{ cursor: 'help', flexShrink: 0, color: 'var(--amplify-colors-neutral-80)' }}
          data-tooltip-id="main-tooltip"
          data-tooltip-content="Opens the export wizard with these matches already selected. There you choose which Edulead fields to write, set the export options, and review every company - which ones are updates and which would be created - before anything is sent to HubSpot."
        />
        <Button
          size='small'
          variation='primary'
          // Amplify resolves `isDisabled ?? isLoading`, so a boolean here means isLoading
          // never disables - without isCreating a second click starts a second export.
          isDisabled={isCreating || overLimit || institutionsIncluded === 0}
          isLoading={isCreating}
          loadingText='Creating…'
          onClick={onCreateExport}
        >
          Create export →
        </Button>
      </Flex>
    </div>
  );
}
